import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtUSD, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader, KpiCard } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { Landmark, CheckCircle2, AlertCircle, Wallet } from "lucide-react";

export default function BankReconciliation() {
  const { user } = useAuth();
  const canManage = ["admin", "finance_officer"].includes(user?.role);
  const [accounts, setAccounts] = useState(null);
  const [activeId, setActiveId] = useState("");
  const [recon, setRecon] = useState(null);
  const [txns, setTxns] = useState([]);
  const [acctOpen, setAcctOpen] = useState(false);
  const [txnOpen, setTxnOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [acctForm, setAcctForm] = useState({ bank_name: "", account_type: "Checking", masked_number: "", opening_balance: 0 });
  const [txnForm, setTxnForm] = useState({ txn_date: new Date().toISOString().slice(0, 10), description: "", reference: "", amount: "", direction: "Deposit" });

  const loadAccounts = useCallback(async () => {
    const { data } = await api.get("/bank-accounts");
    setAccounts(data);
    if (data.length && !activeId) setActiveId(data[0].id);
  }, [activeId]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    const [r, t] = await Promise.all([
      api.get(`/bank-accounts/${id}/reconciliation`),
      api.get(`/bank-transactions?bank_account_id=${id}`),
    ]);
    setRecon(r.data);
    setTxns(t.data);
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { loadDetail(activeId); }, [activeId, loadDetail]);

  const addAccount = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/bank-accounts", { ...acctForm, opening_balance: Number(acctForm.opening_balance) });
      toast.success("Bank account added");
      setAcctOpen(false);
      setAcctForm({ bank_name: "", account_type: "Checking", masked_number: "", opening_balance: 0 });
      await loadAccounts();
      setActiveId(data.id);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const addTxn = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/bank-transactions", { ...txnForm, bank_account_id: activeId, amount: Number(txnForm.amount) });
      toast.success("Transaction added");
      setTxnOpen(false);
      setTxnForm({ ...txnForm, description: "", reference: "", amount: "" });
      loadDetail(activeId); loadAccounts();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const toggleMatch = async (t) => {
    const next = t.status === "Matched" ? "Unmatched" : "Matched";
    setTxns((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: next } : x)));
    try { await api.patch(`/bank-transactions/${t.id}`, { status: next }); loadDetail(activeId); loadAccounts(); }
    catch (err) { toast.error(apiError(err)); loadDetail(activeId); }
  };

  if (!accounts) return <Loader />;

  const columns = [
    { key: "txn_date", label: "Date", render: (r) => fmtDate(r.txn_date) },
    { key: "description", label: "Description" },
    { key: "reference", label: "Reference", className: "font-mono text-xs text-[#5C5C54]" },
    { key: "direction", label: "Type", render: (r) => <StatusBadge tone={r.direction === "Deposit" ? "green" : "orange"}>{r.direction}</StatusBadge> },
    { key: "amount", label: "Amount", align: "right", render: (r) => <span className={r.direction === "Deposit" ? "text-inflow" : "text-outflow"}>{r.direction === "Deposit" ? "+" : "−"}{fmtUSD(r.amount)}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge tone={r.status === "Matched" ? "green" : "amber"}>{r.status}</StatusBadge> },
    { key: "actions", label: "", align: "right", render: (r) => canManage
      ? <button onClick={() => toggleMatch(r)} data-testid={`match-${r.id}`} className="text-xs text-forest font-medium hover:underline">{r.status === "Matched" ? "Unmatch" : "Match"}</button>
      : null },
  ];

  return (
    <div>
      <PageHeader title="Bank Reconciliation" subtitle="Match bank statement activity against the general ledger to certify balances."
        action={canManage && (
          <div className="flex items-center gap-3">
            {activeId && <AddButton onClick={() => setTxnOpen(true)} label="Add Transaction" testid="add-txn-btn" />}
            <button onClick={() => setAcctOpen(true)} data-testid="add-account-btn"
              className="bg-white border border-stone-300 text-stone-800 hover:bg-stone-50 rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200">+ Bank Account</button>
          </div>
        )} />

      {accounts.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-stone-300 rounded-xl bg-white/50">
          <div className="font-heading text-lg">No bank accounts yet</div>
          <div className="text-sm text-[#5C5C54] mt-1">Add a bank account to begin reconciling.</div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            {accounts.map((a) => (
              <button key={a.id} onClick={() => setActiveId(a.id)} data-testid={`account-tab-${a.id}`}
                className={`px-4 py-2 rounded-lg text-sm border transition-colors duration-200 ${activeId === a.id ? "bg-forest text-white border-forest" : "bg-white border-stone-300 text-stone-700 hover:bg-stone-50"}`}>
                {a.bank_name} <span className="opacity-70">{a.masked_number}</span>
              </button>
            ))}
          </div>

          {recon && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-6">
              <KpiCard label="Opening Balance" value={fmtUSD(recon.opening_balance)} icon={Wallet} />
              <KpiCard label="Statement Balance" value={fmtUSD(recon.statement_balance)} icon={Landmark} accent="#178070" />
              <KpiCard label="Cleared Balance" value={fmtUSD(recon.cleared_balance)} icon={CheckCircle2} accent="#2E7D32" />
              <KpiCard label="Outstanding" value={fmtUSD(recon.outstanding_amount)} icon={AlertCircle} accent="#C2410C" />
              <div className="bg-white border border-stone-200 rounded-xl p-6 flex flex-col justify-center items-center">
                <StatusBadge tone={recon.reconciled ? "green" : "amber"} testid="recon-status">{recon.reconciled ? "Reconciled" : `${recon.unmatched_count} Unmatched`}</StatusBadge>
                <div className="text-xs text-[#5C5C54] mt-2">{recon.matched_count} matched</div>
              </div>
            </div>
          )}

          <DataTable columns={columns} rows={txns} testid="bank-txns-table" empty={{ title: "No transactions", hint: "Import or add statement lines." }} />
        </>
      )}

      <Modal open={acctOpen} onOpenChange={setAcctOpen} title="Add Bank Account" onSubmit={addAccount} busy={busy} testid="account-modal">
        <Field label="Bank Name" span={2}><TextInput required value={acctForm.bank_name} onChange={(e) => setAcctForm({ ...acctForm, bank_name: e.target.value })} /></Field>
        <Field label="Account Type"><SelectInput value={acctForm.account_type} onChange={(e) => setAcctForm({ ...acctForm, account_type: e.target.value })} options={["Checking", "Savings", "Money Market"]} /></Field>
        <Field label="Masked Number"><TextInput value={acctForm.masked_number} onChange={(e) => setAcctForm({ ...acctForm, masked_number: e.target.value })} placeholder="****1234" /></Field>
        <Field label="Opening Balance" span={2}><TextInput type="number" step="0.01" value={acctForm.opening_balance} onChange={(e) => setAcctForm({ ...acctForm, opening_balance: e.target.value })} /></Field>
      </Modal>

      <Modal open={txnOpen} onOpenChange={setTxnOpen} title="Add Bank Transaction" onSubmit={addTxn} busy={busy} testid="txn-modal">
        <Field label="Date"><TextInput type="date" required value={txnForm.txn_date} onChange={(e) => setTxnForm({ ...txnForm, txn_date: e.target.value })} /></Field>
        <Field label="Type"><SelectInput value={txnForm.direction} onChange={(e) => setTxnForm({ ...txnForm, direction: e.target.value })} options={["Deposit", "Withdrawal"]} /></Field>
        <Field label="Description" span={2}><TextInput required value={txnForm.description} onChange={(e) => setTxnForm({ ...txnForm, description: e.target.value })} /></Field>
        <Field label="Reference"><TextInput value={txnForm.reference} onChange={(e) => setTxnForm({ ...txnForm, reference: e.target.value })} /></Field>
        <Field label="Amount"><TextInput type="number" step="0.01" required value={txnForm.amount} onChange={(e) => setTxnForm({ ...txnForm, amount: e.target.value })} /></Field>
      </Modal>
    </div>
  );
}
